import { useContext, useEffect, useRef } from 'react';
import { BreadcrumbsContext } from '@/modules/ui/breadcrumbs/BreadcrumbsContext';
import type { BreadcrumbItem } from '@oniratec/onira-react-ui';

type InternalBreadcrumbItem = BreadcrumbItem & {
  __owner: symbol;
};

export function useBreadcrumb(item: BreadcrumbItem) {
  const ctx = useContext(BreadcrumbsContext);
  const ownerRef = useRef<symbol | null>(null);
  const lastLabelRef = useRef<string | undefined>(undefined);

  if (!ctx) {
    throw new Error('useBreadcrumb must be used within provider');
  }

  if (ownerRef.current === null) {
    ownerRef.current = Symbol('breadcrumb-owner');
  }

  useEffect(() => {
    const owner = ownerRef.current!;
    const labelChanged = lastLabelRef.current !== item.label;
    lastLabelRef.current = item.label;

    ctx.setItems(prev => {
      const index = prev.findIndex(
        i => '__owner' in i && (i as InternalBreadcrumbItem).__owner === owner,
      );

      // Ya existe este breadcrumb
      if (index !== -1) {
        const current = prev[index] as InternalBreadcrumbItem;

        // Si el label no cambió, no hacer nada
        if (!labelChanged && current.label === item.label) {
          return prev;
        }

        // Actualizar solo si el label cambió
        const next = [...prev];
        next[index] = { ...item, __owner: owner } as InternalBreadcrumbItem;
        return next;
      }

      // Agregar nuevo breadcrumb (solo la primera vez)
      return [...prev, { ...item, __owner: owner } as InternalBreadcrumbItem];
    });

    return () => {
      ctx.setItems(prev =>
        prev.filter(i => !('__owner' in i) || (i as InternalBreadcrumbItem).__owner !== owner),
      );
    };
    // Solo dependemos de item.label, no de ctx
    // ctx.setItems es estable (viene de useState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.label]);
}
